import type { useT } from '../i18n/useT';
import type { FolderSyncState } from '../lib/folderSync';
import type { SyncReport } from '../lib/folderSync/reconcile';
import { formatDateTime } from '../lib/format';

/**
 * What the last sync did, in one line — shared by the folder and the
 * repository, because they are the same sync with a different file system and
 * saying it two different ways would only suggest otherwise.
 */
export function syncSummary(report: SyncReport, t: ReturnType<typeof useT>): string {
  const parts = [
    [report.imported + report.fromDisk, 'folderSync.fromDisk'],
    [report.exported + report.toDisk, 'folderSync.toDisk'],
    [report.moved, 'folderSync.moved'],
    [report.trashedDocs + report.trashedFiles, 'folderSync.trashed'],
  ] as const;
  return parts
    .filter(([n]) => n > 0)
    .map(([n, key]) => t(key, { n }))
    .join(' · ');
}

/** Where the sync stands: off, stuck, or when it last ran and what it did. */
export function syncStatusLine(
  state: FolderSyncState | null,
  t: ReturnType<typeof useT>,
  offLabel = 'folderSync.off',
): string {
  if (!state?.enabled) return t(offLabel);
  if (state.lastProblem === 'permission') return t('folderSync.permission');
  if (state.lastProblem === 'error') return t('folderSync.error');
  if (!state.lastSyncAt) return t('folderSync.pending');
  return [
    t('folderSync.last', { date: formatDateTime(state.lastSyncAt) }),
    state.lastReport && syncSummary(state.lastReport, t),
  ]
    .filter(Boolean)
    .join(' — ');
}
